// One-time login links ("magic links"), and the sign-in address pre-fill that
// goes with them.
//
// Both are read out of the URL fragment. Everything after '#' is never sent to
// the server, so neither reaches access logs, proxy logs or Referer headers.
// The fragment is erased as soon as the app starts, so that nothing sits in the
// address bar to be copied, read over a shoulder, or offered by its dropdown.
//
// The token is not a credential in a URL of the kind that must not exist: it is
// one-way, short-lived and good only for the account that receives it. The
// pre-fill is not a secret either, it is the address the customer logs in with.
// Neither of them is ever a password.
//
// Exchanging the token for a session happens in middlewares/Authenticated.

const TOKEN_KEY = 'login-token'
const EMAIL_KEY = 'username'
const MAX_TOKEN_LENGTH = 128
// RFC 5321 limit for an address
const MAX_EMAIL_LENGTH = 254

let pendingToken: string | null = null
let prefillEmail = ''

// erases the fragment from the address bar
const eraseFragment = (): void => {
  const { pathname, search } = window.location
  window.history.replaceState(null, '', `${pathname}${search}`)
}

// an address is never allowed to carry control characters or to be unbounded;
// browsers tend to strip these from the URL already, this is the second gate
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_RE = /[\x00-\x1f\x7f]/gu

const sanitizeEmail = (email: string): string => email.replace(CONTROL_CHARS_RE, '').trim().slice(0, MAX_EMAIL_LENGTH)

// Reads the fragment and removes it. Call once, before the app is rendered, so
// that nothing else ever gets to look at it.
export const captureLoginLink = (): void => {
  const hash = window.location.hash
  if (hash.charAt(0) !== '#') {
    return
  }

  // '+' means a plus in an address, not a space; URLSearchParams would decode
  // it as one. Login link tokens are base64url and hold no '+', so they are
  // unaffected.
  const params = new URLSearchParams(hash.slice(1).replace(/\+/gu, '%2B'))
  const token = params.get(TOKEN_KEY)
  const email = params.get(EMAIL_KEY)

  if (token === null && email === null) {
    // a fragment of nobody else's business, leave it where it is
    return
  }

  if (token && token.length <= MAX_TOKEN_LENGTH) {
    pendingToken = token
  }

  if (email) {
    prefillEmail = sanitizeEmail(email)
  }

  eraseFragment()
}

// Returns the captured token and forgets it, so that it is redeemed only once.
// Returns null when the app was opened normally.
export const takePendingLoginLink = (): string | null => {
  const token = pendingToken
  pendingToken = null

  return token
}

// The sign-in address taken from #username=..., for pre-filling the login form.
// Empty when the app was opened without one.
export const getPrefillEmail = (): string => prefillEmail
